/**
 * Risk Intelligence — quantitative risk analytics for the user's portfolio
 * and trading activity.
 *
 * REAL METRICS computed from userActivity aggregator (no fake "success rate"
 * or bot fantasy):
 *
 *   - Concentration: Herfindahl–Hirschman Index (HHI) across exchanges,
 *     chains, pairs, and sources
 *   - Diversification: number of venues + entropy
 *   - Activity consistency: coefficient of variation (CV) of daily volume
 *   - Engagement: active days / total days
 *   - Size anomalies: trades with |z-score| > 2 from user's mean
 *
 * Composite Risk Score (0–100) is a transparent weighted blend, NOT AI.
 */

import { useMemo, useState } from 'react';
import {
  Shield, AlertTriangle, Activity, Layers, Target, Globe, BarChart3,
  RefreshCw, Info, CheckCircle2,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useUserActivity } from '../hooks/useData';
import { Spinner, InfoTip } from '../components/ui';
import { formatUsd } from '../services/api';
import { formatUsdCompact } from '../services/displayPrefs';

const RANGE_OPTIONS = [
  { id: 7,   label: '7d' },
  { id: 30,  label: '30d' },
  { id: 60,  label: '60d' },
  { id: 90,  label: '90d' },
  { id: 'all', label: 'All' },
];

// ─── Risk math ──────────────────────────────────────────────────────────────

function herfindahl(buckets, totalVolume) {
  if (!totalVolume || totalVolume <= 0) return 0;
  let hhi = 0;
  for (const b of buckets) {
    const share = (b.volumeUsd || 0) / totalVolume;
    hhi += share * share * 10000;
  }
  return Math.round(hhi);
}

function hhiInterpretation(hhi) {
  if (hhi >= 8000) return { label: 'EXTREME', color: 'text-red-400', desc: 'Almost all volume in one place' };
  if (hhi >= 5000) return { label: 'HIGH',    color: 'text-amber-400', desc: 'One venue dominates' };
  if (hhi >= 2500) return { label: 'MODERATE', color: 'text-amber-400', desc: 'Some concentration' };
  if (hhi >= 1500) return { label: 'LOW',     color: 'text-emerald-400', desc: 'Well distributed' };
  return { label: 'MINIMAL', color: 'text-emerald-400', desc: 'Highly diversified' };
}

function coefficientOfVariation(values) {
  if (!values.length) return null;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  if (mean === 0) return null;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance) / mean;
}

function zScores(values) {
  if (values.length < 2) return values.map(() => 0);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const stdev = Math.sqrt(values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length);
  if (stdev === 0) return values.map(() => 0);
  return values.map(v => (v - mean) / stdev);
}

function computeRiskScore({ hhiExchange, hhiSymbol, activeDays, totalDays, anomalyCount, totalTrades, cv }) {
  const concentrationScore = Math.max(0, 100 - (((hhiExchange + hhiSymbol) / 2) / 100));
  const engagementScore = Math.min(100, (activeDays / Math.max(1, totalDays * 0.5)) * 100);
  const consistencyScore = cv === null ? 50 : Math.max(0, 100 - (Math.min(cv, 3) * 33.3));
  const anomalyScore = totalTrades > 0
    ? Math.max(0, 100 - (anomalyCount / totalTrades * 100 * 3))
    : 100;
  const composite = concentrationScore * 0.35 + engagementScore * 0.20 + consistencyScore * 0.25 + anomalyScore * 0.20;
  return {
    score: Math.round(composite),
    components: {
      concentration: Math.round(concentrationScore),
      engagement: Math.round(engagementScore),
      consistency: Math.round(consistencyScore),
      anomaly: Math.round(anomalyScore),
    },
  };
}

function scoreLabel(score) {
  if (score >= 75) return { label: 'STRONG', color: '#10b981', bg: 'bg-emerald-500/10 border-emerald-500/30' };
  if (score >= 55) return { label: 'MODERATE', color: '#f59e0b', bg: 'bg-amber-500/10 border-amber-500/30' };
  if (score >= 35) return { label: 'ELEVATED', color: '#f97316', bg: 'bg-orange-500/10 border-orange-500/30' };
  return { label: 'HIGH RISK', color: '#ef4444', bg: 'bg-red-500/10 border-red-500/30' };
}

// ─── UI helpers ─────────────────────────────────────────────────────────────

function Section({ title, icon: Icon, hint, children, action }) {
  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-amber-400" />} {title}
        </h3>
        {action || (hint && <span className="text-[10px] text-gray-500 italic">{hint}</span>)}
      </div>
      {children}
    </div>
  );
}

function MetricRow({ label, value, hint, accent = 'white', formula }) {
  const c = { white: 'text-white', emerald: 'text-emerald-400', amber: 'text-amber-400', red: 'text-red-400', cyan: 'text-cyan-400' };
  return (
    <div className="flex items-center justify-between py-2 border-b border-dark-700/30 last:border-0">
      <div>
        <span className="text-sm text-gray-300">{label}</span>
        {hint && <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="text-right">
        <span className={`text-sm font-mono font-bold ${c[accent]}`}>{value}</span>
        {formula && <p className="text-[10px] text-gray-600 mt-0.5 font-mono">{formula}</p>}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function RiskIntelligencePage() {
  const [range, setRange] = useState(30);
  const { summary, loading, error, refresh } = useUserActivity({
    recent: 100,
    days: typeof range === 'number' ? range : 90,
    window: range === 'all' ? null : range,
  });
  const [activeTab, setActiveTab] = useState('overview');

  const analysis = useMemo(() => {
    if (!summary) return null;
    const windowSum = summary.window || summary.allTime || {};
    const totalVolume = windowSum.volumeUsd || 0;
    const totalTrades = windowSum.tradesCount || 0;

    const hhiExchange = herfindahl(summary.byExchange || [], totalVolume);
    const hhiChain    = herfindahl(summary.byChain || [], totalVolume);
    const hhiSymbol   = herfindahl(summary.bySymbol || [], totalVolume);
    const hhiSource   = herfindahl(summary.bySource || [], totalVolume);

    const totalDays = (typeof range === 'number') ? range : (summary.timeSeries?.length || 30);
    const activeDays = (summary.timeSeries || []).filter(d => d.trades > 0).length;

    const dailyVols = (summary.timeSeries || []).filter(d => d.volumeUsd > 0).map(d => d.volumeUsd);
    const cv = coefficientOfVariation(dailyVols);

    const tradeVols = (summary.recentTrades || []).map(t => Number(t.amountQuote) || 0).filter(v => v > 0);
    const zs = zScores(tradeVols);
    const sizeAnomalies = zs.filter(z => Math.abs(z) > 2).length;

    const topExchange = summary.byExchange?.[0];
    const topSymbol = summary.bySymbol?.[0];
    const topShare = totalVolume > 0 && topSymbol ? (topSymbol.volumeUsd / totalVolume * 100) : 0;

    const risk = computeRiskScore({ hhiExchange, hhiSymbol, activeDays, totalDays, anomalyCount: sizeAnomalies, totalTrades, cv });

    const recs = [];
    if (hhiExchange > 5000 && (summary.byExchange?.length || 0) > 0) {
      recs.push({
        level: 'warning',
        title: 'Single-venue concentration',
        body: `${Math.round(((summary.byExchange[0]?.volumeUsd || 0) / Math.max(1, totalVolume)) * 100)}% of your volume is on ${summary.byExchange[0]?.key}. If that exchange experiences an outage or you lose API access, you lose access to most of your activity. Consider routing some trades through a second exchange.`,
      });
    }
    if (hhiSymbol > 6000 && topSymbol) {
      recs.push({
        level: 'warning',
        title: 'Single-pair concentration',
        body: `${Math.round(topShare)}% of your volume is in ${topSymbol.key}. Diversifying into 2–3 uncorrelated pairs reduces single-asset shock risk.`,
      });
    }
    if (activeDays < totalDays * 0.2 && totalTrades > 0) {
      recs.push({
        level: 'info',
        title: 'Sporadic activity',
        body: `You traded on ${activeDays} of ${totalDays} days. Sporadic activity is fine if intentional, but if you're trying to maintain a strategy, consider scheduled execution (Algo Orders TWAP/DCA) for consistency.`,
      });
    }
    if (cv !== null && cv > 2 && dailyVols.length > 3) {
      recs.push({
        level: 'info',
        title: 'High day-to-day variance',
        body: `Your daily trading volume varies a lot (CV = ${cv.toFixed(2)}). Some days are dramatically larger than others. If unintentional, position-sizing rules can help (Smart Trade caps).`,
      });
    }
    if (sizeAnomalies > 0) {
      recs.push({
        level: 'info',
        title: `${sizeAnomalies} unusually-sized trades detected`,
        body: `${sizeAnomalies} of your last ${tradeVols.length} trades were more than 2σ from your typical size. Review them in the Anomalies tab — outsized trades dominate P&L exposure.`,
      });
    }
    if (!recs.length && totalTrades > 0) {
      recs.push({
        level: 'success',
        title: 'No risk flags',
        body: 'Your trading profile shows balanced concentration, consistent activity, and no anomalous trades in the current window. Keep monitoring as your portfolio grows.',
      });
    }

    return {
      totalVolume, totalTrades, totalDays, activeDays,
      hhiExchange, hhiChain, hhiSymbol, hhiSource,
      cv, sizeAnomalies, topExchange, topSymbol, topShare,
      risk, recs, tradeVols, zs,
    };
  }, [summary, range]);

  if (loading) return <div className="flex items-center justify-center h-96"><Spinner size="lg" className="text-accent-primary" /></div>;
  if (error) return (
    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
      {error} <button onClick={refresh} className="ml-3 underline">Retry</button>
    </div>
  );

  const hasActivity = (summary?.counts?.total || 0) > 0;
  const sLabel = analysis ? scoreLabel(analysis.risk.score) : null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
            <Shield className="w-6 h-6" /> Risk Intelligence
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Quantitative risk analytics — concentration, diversification, consistency, anomalies. Every metric transparently computed from your real activity.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700">
            {RANGE_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => setRange(opt.id)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-all ${range === opt.id ? 'bg-amber-400 text-dark-900' : 'text-gray-400 hover:text-white'}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <button onClick={refresh} className="text-xs text-gray-500 hover:text-white px-3 py-1.5 rounded-lg bg-dark-800 border border-dark-700 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {!hasActivity ? (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-12 text-center">
          <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-medium">No risk to analyze yet</p>
          <p className="text-gray-500 text-sm mt-1">Place trades and the full risk suite populates from your real activity.</p>
        </div>
      ) : analysis && (
        <>
          {/* Risk score hero */}
          <div className={`rounded-2xl border p-6 ${sLabel.bg}`}>
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke={sLabel.color} strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 42}`}
                      strokeDashoffset={`${2 * Math.PI * 42 * (1 - analysis.risk.score / 100)}`}
                      transform="rotate(-90 50 50)" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold font-mono text-white">{analysis.risk.score}</span>
                    <span className="text-[10px] text-gray-500">/ 100</span>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border" style={{ color: sLabel.color, borderColor: sLabel.color + '50' }}>{sLabel.label}</span>
                  <p className="text-white font-bold text-lg mt-1 flex items-center gap-2">
                    Portfolio Risk Score
                    <InfoTip side="right">Higher = lower risk. 75-100 STRONG · 55-74 MODERATE · 35-54 ELEVATED · 0-34 HIGH RISK. Weighted average of the 4 sub-scores on the right.</InfoTip>
                  </p>
                  <p className="text-xs text-gray-400">Higher = lower risk. Transparent weighted composite (see Methodology).</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                {[
                  { label: 'Concentration', val: analysis.risk.components.concentration, color: 'text-amber-400', info: 'Derived from HHI across exchanges + pairs. Lower HHI = better diversification = higher sub-score. Formula in Methodology tab.' },
                  { label: 'Engagement',    val: analysis.risk.components.engagement,    color: 'text-cyan-400', info: 'Active-day ratio: how many days in the window you placed at least one trade. Passive/sporadic = lower; daily-active = higher.' },
                  { label: 'Consistency',   val: analysis.risk.components.consistency,   color: 'text-emerald-400', info: 'Derived from CV (σ/μ) of daily volume. Steady daily volume = higher. Wildly swinging = lower. CV > 2 often signals impulse trading.' },
                  { label: 'Anomaly-free',  val: analysis.risk.components.anomaly,       color: 'text-violet-400', info: 'Fraction of trades within 2σ of your typical size. Outliers drag this down — but conviction trades are also outliers, so review each.' },
                ].map(c => (
                  <div key={c.label} className="bg-dark-900/50 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      {c.label}
                      <InfoTip side="bottom">{c.info}</InfoTip>
                    </p>
                    <p className={`text-xl font-bold font-mono ${c.color}`}>{c.val}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              { id: 'overview',        label: 'Overview',        icon: BarChart3 },
              { id: 'concentration',   label: 'Concentration',   icon: Target },
              { id: 'anomalies',       label: 'Anomalies',       icon: AlertTriangle },
              { id: 'recommendations', label: 'Recommendations', icon: Info },
              { id: 'methodology',     label: 'Methodology',     icon: Shield },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === t.id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section title="Concentration Snapshot" icon={Target} hint="HHI 0 = perfectly diversified · 10000 = single-venue">
                <MetricRow label="HHI — by Exchange" value={analysis.hhiExchange.toLocaleString()} hint={hhiInterpretation(analysis.hhiExchange).desc}
                  accent={hhiInterpretation(analysis.hhiExchange).label === 'EXTREME' || hhiInterpretation(analysis.hhiExchange).label === 'HIGH' ? 'red' : 'emerald'} />
                <MetricRow label="HHI — by Pair" value={analysis.hhiSymbol.toLocaleString()} hint={hhiInterpretation(analysis.hhiSymbol).desc}
                  accent={hhiInterpretation(analysis.hhiSymbol).label === 'EXTREME' || hhiInterpretation(analysis.hhiSymbol).label === 'HIGH' ? 'red' : 'emerald'} />
                <MetricRow label="HHI — by Chain" value={analysis.hhiChain.toLocaleString()} hint={hhiInterpretation(analysis.hhiChain).desc}
                  accent={hhiInterpretation(analysis.hhiChain).label === 'EXTREME' || hhiInterpretation(analysis.hhiChain).label === 'HIGH' ? 'red' : 'emerald'} />
                <MetricRow label="HHI — by Strategy" value={analysis.hhiSource.toLocaleString()} hint={hhiInterpretation(analysis.hhiSource).desc}
                  accent={hhiInterpretation(analysis.hhiSource).label === 'EXTREME' || hhiInterpretation(analysis.hhiSource).label === 'HIGH' ? 'red' : 'emerald'} />
              </Section>

              <Section title="Activity Profile" icon={Activity}>
                <MetricRow label="Total Trades" value={analysis.totalTrades.toLocaleString()} accent="cyan" />
                <MetricRow label="Total Volume" value={formatUsd(analysis.totalVolume)} accent="cyan" />
                <MetricRow label="Active Days" value={`${analysis.activeDays} / ${analysis.totalDays}`}
                  hint={analysis.activeDays >= analysis.totalDays * 0.5 ? 'High engagement' : 'Sporadic activity'}
                  accent={analysis.activeDays >= analysis.totalDays * 0.5 ? 'emerald' : 'amber'} />
                <MetricRow label="Day-to-day variance (CV)" value={analysis.cv === null ? '—' : analysis.cv.toFixed(2)}
                  hint={analysis.cv === null ? 'Need more data' : analysis.cv < 1 ? 'Steady' : analysis.cv < 2 ? 'Moderate variance' : 'High variance'}
                  accent={analysis.cv === null ? 'white' : analysis.cv < 1 ? 'emerald' : analysis.cv < 2 ? 'amber' : 'red'} formula="σ / μ" />
                <MetricRow label="Size anomalies (|z| > 2)" value={`${analysis.sizeAnomalies} of ${analysis.tradeVols.length}`}
                  hint="Trades more than 2 standard deviations from your typical size"
                  accent={analysis.sizeAnomalies === 0 ? 'emerald' : 'amber'} />
              </Section>
            </div>
          )}

          {activeTab === 'concentration' && (
            <>
              <Section title="Volume Distribution by Pair" icon={Target}>
                {summary.bySymbol?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={summary.bySymbol.slice(0, 10)} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis type="number" stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false}
                        tickFormatter={formatUsdCompact} />
                      <YAxis dataKey="key" type="category" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} width={100} />
                      <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                        formatter={v => `$${Number(v).toLocaleString()}`} />
                      <Bar dataKey="volumeUsd" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-gray-500 py-8 text-center">No pairs traded yet.</p>}
              </Section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="By Exchange (CEX)" icon={Globe}>
                  {summary.byExchange?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={summary.byExchange} dataKey="volumeUsd" nameKey="key" cx="50%" cy="50%" outerRadius={80} innerRadius={50}>
                          {summary.byExchange.map((_, i) => (
                            <Cell key={i} fill={['#f59e0b', '#06b6d4', '#10b981', '#8b5cf6', '#ef4444', '#ec4899'][i % 6]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v, n) => [`$${Number(v).toLocaleString()}`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-gray-500 py-8 text-center">No CEX trades yet.</p>}
                </Section>

                <Section title="By Strategy" icon={Layers}>
                  {summary.bySource?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={summary.bySource} dataKey="volumeUsd" nameKey="key" cx="50%" cy="50%" outerRadius={80} innerRadius={50}>
                          {summary.bySource.map((_, i) => (
                            <Cell key={i} fill={['#10b981', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#ec4899', '#a78bfa', '#3b82f6'][i % 8]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v, n) => [`$${Number(v).toLocaleString()}`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-gray-500 py-8 text-center">No strategy data yet.</p>}
                </Section>
              </div>
            </>
          )}

          {activeTab === 'anomalies' && (
            <Section title="Trade Size Outliers" icon={AlertTriangle} hint="Trades with |z-score| > 2 from your mean">
              {analysis.tradeVols.length < 3 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-emerald-300">Not enough trades to detect anomalies yet</p>
                  <p className="text-[11px] text-gray-500 mt-1">Need at least 3 trades for statistical analysis</p>
                </div>
              ) : analysis.sizeAnomalies === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-emerald-300">No anomalous trade sizes detected</p>
                  <p className="text-[11px] text-gray-500 mt-1">All recent trades are within 2σ of your typical size</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(summary.recentTrades || [])
                    .map((t, i) => ({ ...t, z: analysis.zs[i] || 0 }))
                    .filter(t => Math.abs(t.z) > 2)
                    .slice(0, 20)
                    .map((t, i) => (
                      <div key={i} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2 border-l-2 border-amber-500/50">
                        <div className="flex-1">
                          <p className="text-xs text-white font-mono">{t.symbol || '—'} · {t.side || '—'}</p>
                          <p className="text-[10px] text-gray-500">{t.exchange || t.chain || '—'} · {t.ts ? new Date(t.ts).toLocaleString() : '—'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-mono text-white">{formatUsd(t.amountQuote || 0)}</p>
                          <p className={`text-[10px] font-mono ${t.z > 0 ? 'text-amber-400' : 'text-cyan-400'}`}>
                            z = {t.z > 0 ? '+' : ''}{t.z.toFixed(2)}σ
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </Section>
          )}

          {activeTab === 'recommendations' && (
            <div className="space-y-3">
              {analysis.recs.map((r, i) => {
                const c = r.level === 'warning' ? { border: 'border-amber-500/30', bg: 'bg-amber-500/5', icon: AlertTriangle, color: 'text-amber-400' }
                       : r.level === 'success' ? { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', icon: CheckCircle2, color: 'text-emerald-400' }
                       : { border: 'border-cyan-500/30', bg: 'bg-cyan-500/5', icon: Info, color: 'text-cyan-400' };
                const Icon = c.icon;
                return (
                  <div key={i} className={`rounded-2xl border p-4 ${c.border} ${c.bg}`}>
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${c.color} flex-shrink-0 mt-0.5`} />
                      <div>
                        <p className={`text-sm font-semibold ${c.color}`}>{r.title}</p>
                        <p className="text-xs text-gray-300 mt-1 leading-relaxed">{r.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'methodology' && (
            <Section title="How we compute each metric" icon={Shield}>
              <div className="space-y-3 text-xs text-gray-300 leading-relaxed">
                <div>
                  <p className="text-white font-semibold mb-1">Herfindahl–Hirschman Index (HHI)</p>
                  <p>Sum of squared market shares × 10,000. Standard concentration metric used by antitrust regulators. HHI = Σ(shareᵢ)². Range 0–10,000: lower = more diversified, higher = more concentrated. We compute it separately for exchanges, chains, pairs, and strategies.</p>
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Coefficient of Variation (CV)</p>
                  <p>Standard deviation divided by mean (σ/μ) of your daily trading volume. A CV of 0 means perfectly consistent activity. CV {`<`} 1 = steady; CV {`>`} 2 = highly variable (some days much larger than others).</p>
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Z-Score Anomaly Detection</p>
                  <p>For each trade size, z = (size − μ) / σ. Trades with |z| {`>`} 2 sit in the top/bottom 2.5% of your distribution — statistically unusual. Useful for spotting fat-finger errors or strategy drift.</p>
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Composite Risk Score (0–100)</p>
                  <p>Transparent weighted blend: concentration (35%), engagement (20%), consistency (25%), anomaly-free rate (20%). Each component normalized to 0–100. NOT machine learning — every input is named and computed in code you can audit.</p>
                </div>
                <div className="bg-dark-700/40 rounded-xl p-3 mt-4">
                  <p className="text-[11px] text-gray-400 italic">
                    What's NOT here: VaR, Sharpe, max drawdown, Sortino, Calmar — these all require accurate cost-basis accounting (FIFO/LIFO/HIFO across all accounts).
                    That engine is in flight; until it lands, we show only what we can measure reliably.
                  </p>
                </div>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
